import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    IconButton,
    Alert,
    Snackbar,
    ToggleButtonGroup,
    ToggleButton,
    Stack,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CompressIcon from "@mui/icons-material/Compress";
import CloseIcon from "@mui/icons-material/Close";
import { ToolPageShell } from "./ToolPageShell";
import { TOOLS, formatFileSize, PdfQuality } from "../util/config";
import { colors } from "../theme/theme";
import { pdfApi, statsApi } from "../api/client";

const tool = TOOLS.find((t) => t.id === "pdf-compress");

export const PdfCompressPage = () => {
    const [pdf, setPdf] = useState(null);
    const [isCompressing, setIsCompressing] = useState(false);
    const [selectedQuality, setSelectedQuality] = useState(PdfQuality.LOW);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file && file.type === "application/pdf") {
            setPdf(file);
            showSnackbar("PDF uploaded successfully!", "success");
        } else {
            showSnackbar("Please upload a valid PDF", "error");
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "application/pdf": [".pdf"] },
        multiple: false,
    });

    const compressPdf = async () => {
        if (!pdf) return showSnackbar("Please upload a PDF first", "error");
        setIsCompressing(true);
        try {
            const blob = await pdfApi.compress(pdf, selectedQuality);
            statsApi.logExport(tool.id, blob.size);
            showSnackbar("PDF compressed and downloaded!", "success");
        } catch (error) {
            console.error(error);
            showSnackbar("Failed to compress PDF", "error");
        } finally {
            setIsCompressing(false);
        }
    };

    const clearPdf = () => setPdf(null);

    const qualityLabels = {
        [PdfQuality.LOW]: { label: "Maximum compression", hint: "Smallest size" },
        [PdfQuality.MEDIUM]: { label: "Balanced", hint: "Good size/quality" },
        [PdfQuality.HIGH]: { label: "Best quality", hint: "Larger file" },
    };

    return (
        <ToolPageShell
            tool={tool}
            tips={[
                "Maximum compression is great for email attachments.",
                "Use Best quality for documents you'll print.",
                "Compression mostly affects embedded images, not text.",
            ]}
        >
            {!pdf ? (
                <Box
                    {...getRootProps()}
                    sx={{
                        border: "2px dashed",
                        borderColor: isDragActive ? "primary.main" : colors.border,
                        borderRadius: 2,
                        padding: 6,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: isDragActive ? colors.accentSoft : colors.surfaceAlt,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "primary.main", bgcolor: colors.accentSoft },
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ fontSize: 56, color: isDragActive ? "primary.main" : colors.textMuted, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isDragActive ? "Drop your PDF" : "Drag & drop a PDF"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textMuted }}>
                        or click to browse
                    </Typography>
                </Box>
            ) : (
                <Box>
                    <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: colors.surfaceAlt, border: `1px solid ${colors.border}`, position: "relative" }}>
                        <IconButton
                            sx={{ position: "absolute", top: 8, right: 8 }}
                            size="small"
                            onClick={clearPdf}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <PictureAsPdfIcon sx={{ fontSize: 40, color: "error.main" }} />
                            <Box flex={1} minWidth={0}>
                                <Typography sx={{ fontWeight: 600 }} noWrap>{pdf.name}</Typography>
                                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono"' }}>
                                    {formatFileSize(pdf.size)}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 1 }}>
                        Compression level
                    </Typography>
                    <ToggleButtonGroup
                        value={selectedQuality}
                        exclusive
                        onChange={(_, v) => v && setSelectedQuality(v)}
                        sx={{ mb: 3, width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}
                    >
                        {[PdfQuality.LOW, PdfQuality.MEDIUM, PdfQuality.HIGH].map((q) => (
                            <ToggleButton
                                key={q}
                                value={q}
                                sx={{
                                    flexDirection: "column",
                                    py: 1.5,
                                    border: `1px solid ${colors.border} !important`,
                                    borderRadius: "8px !important",
                                    "&.Mui-selected": {
                                        bgcolor: colors.accentSoft,
                                        borderColor: `${tool.accent} !important`,
                                        "&:hover": { bgcolor: colors.accentSoft },
                                    },
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: 13, textTransform: "none" }}>
                                    {qualityLabels[q].label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: "none" }}>
                                    {qualityLabels[q].hint}
                                </Typography>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={compressPdf}
                            disabled={isCompressing}
                            startIcon={isCompressing ? <CircularProgress size={20} color="inherit" /> : <CompressIcon />}
                        >
                            {isCompressing ? "Compressing..." : "Compress PDF"}
                        </Button>
                        <Button variant="outlined" size="large" onClick={clearPdf}>
                            Choose another
                        </Button>
                    </Box>
                </Box>
            )}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </ToolPageShell>
    );
};
